import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import StripeConnectCard from '../components/settings/StripeConnectCard';
import PortalSettingsCard from '../components/settings/PortalSettingsCard';
import RoutingMemoryCard from '../components/settings/RoutingMemoryCard';
import SiteEditorCard from '../components/settings/SiteEditorCard';
import GoogleIntegrationCard from '../components/settings/GoogleIntegrationCard';
import ReceiptsForwardingCard from '../components/settings/ReceiptsForwardingCard';
import PaymentSettingsCard from '../components/settings/PaymentSettingsCard';
import DashboardDefaultRangeCard from '../components/settings/DashboardDefaultRangeCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { HelpPanel } from '../components/ui/help-panel';
import TemplatePermissionsCard from '../components/TemplatePermissionsCard';
import { settingsHelp } from '../lib/helpContent';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Building2,
  Users,
  UserPlus,
  Shield,
  Link2,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Smartphone,
  Key,
  Copy,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  FileText,
  Image as ImageIcon,
  Palette,
  X,
  Mail,
  Phone,
  MapPin,
  Globe,
  Sparkles,
  Activity,
  Server,
  CreditCard,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { MCPSettings } from '../components/settings/MCPSettings';
import { PerformanceDashboard } from '../components/settings/PerformanceDashboard';
import { BillingSettings } from '../components/settings/BillingSettings';
import MyAccessPanel from '../components/MyAccessPanel';
import MemberActivityTimeline from '../components/MemberActivityTimeline';
import AdminPanel from './AdminPanel';
import LiveInvoicePreview from '../components/settings/LiveInvoicePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_AVATAR_URL = (userId) => `${API_URL}/api/users/${userId}/avatar`;

const roleColors = {
  owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  accountant: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  bookkeeper: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  read_only: 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200',
  client_portal: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
};

// MFA Settings Component
const MFASettings = ({ user, onUpdate }) => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [regenerateCode, setRegenerateCode] = useState('');

  // Fetch MFA status
  useEffect(() => {
    const fetchMfaStatus = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/auth/mfa/status`);
        if (response.ok) {
          const data = await response.json();
          setMfaStatus(data);
        }
      } catch (error) {
        console.error('Failed to fetch MFA status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMfaStatus();
  }, []);

  // Start MFA setup
  const handleSetupMFA = async () => {
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/mfa/setup`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setBackupCodes(data.backup_codes);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to setup MFA');
      }
    } catch (error) {
      toast.error('Failed to setup MFA');
    } finally {
      setProcessing(false);
    }
  };

  // Verify MFA setup
  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/mfa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: verifyCode })
      });
      if (response.ok) {
        toast.success('MFA enabled successfully!');
        setSetupData(null);
        setVerifyCode('');
        setShowBackupCodes(true);
        // Refresh status
        const statusRes = await apiFetch(`${API_URL}/api/auth/mfa/status`);
        if (statusRes.ok) setMfaStatus(await statusRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setProcessing(false);
    }
  };

  // Disable MFA
  const handleDisableMFA = async () => {
    if (!disableCode) {
      toast.error('Please enter your MFA code or backup code');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/mfa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: disableCode })
      });
      if (response.ok) {
        toast.success('MFA disabled');
        setDisableCode('');
        setMfaStatus({ mfa_enabled: false });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to disable MFA');
    } finally {
      setProcessing(false);
    }
  };

  // Regenerate backup codes
  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Please enter your current MFA code');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/mfa/regenerate-backup-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: regenerateCode })
      });
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes);
        setShowBackupCodes(true);
        setRegenerateCode('');
        toast.success('New backup codes generated');
        // Refresh status
        const statusRes = await apiFetch(`${API_URL}/api/auth/mfa/status`);
        if (statusRes.ok) setMfaStatus(await statusRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to regenerate codes');
    } finally {
      setProcessing(false);
    }
  };

  // Copy backup codes
  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* MFA Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication (2FA)
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account by requiring a verification code from your authenticator app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              {mfaStatus?.mfa_enabled ? (
                <ShieldCheck className="w-8 h-8 text-emerald-500" />
              ) : (
                <ShieldOff className="w-8 h-8 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">
                  {mfaStatus?.mfa_enabled ? '2FA is Enabled' : '2FA is Disabled'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {mfaStatus?.mfa_enabled 
                    ? `${mfaStatus.backup_codes_remaining} backup codes remaining`
                    : 'Your account is less secure without 2FA'
                  }
                </p>
              </div>
            </div>
            {mfaStatus?.mfa_enabled ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Active
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                Not Active
              </Badge>
            )}
          </div>

          {/* Setup MFA */}
          {!mfaStatus?.mfa_enabled && !setupData && (
            <Button onClick={handleSetupMFA} disabled={processing} data-testid="enable-mfa-btn">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
              Enable Two-Factor Authentication
            </Button>
          )}

          {/* MFA Setup Flow */}
          {setupData && (
            <div className="space-y-4 border rounded-lg p-4">
              <h4 className="font-medium">Setup Two-Factor Authentication</h4>
              
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={setupData.qr_code} alt="MFA QR Code" className="w-48 h-48" />
                </div>
                
                <p className="text-sm text-muted-foreground">
                  2. Or manually enter this secret key: 
                  <code className="ml-2 px-2 py-1 bg-muted rounded text-xs">{setupData.secret}</code>
                </p>
                
                <p className="text-sm text-muted-foreground">
                  3. Enter the 6-digit code from your authenticator app to verify:
                </p>
                
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-32 font-mono text-center text-lg tracking-widest"
                    maxLength={6}
                    data-testid="mfa-verify-code"
                  />
                  <Button onClick={handleVerifySetup} disabled={processing || verifyCode.length !== 6}>
                    {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Verify & Enable
                  </Button>
                  <Button variant="outline" onClick={() => setSetupData(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Disable MFA */}
          {mfaStatus?.mfa_enabled && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium text-destructive">Disable Two-Factor Authentication</h4>
              <p className="text-sm text-muted-foreground">
                Enter your current MFA code or a backup code to disable 2FA.
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.toUpperCase())}
                  className="w-40 font-mono"
                  data-testid="mfa-disable-code"
                />
                <Button variant="destructive" onClick={handleDisableMFA} disabled={processing}>
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Disable 2FA
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup Codes Card */}
      {(showBackupCodes || mfaStatus?.mfa_enabled) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Backup Codes
            </CardTitle>
            <CardDescription>
              Save these codes in a secure place. You can use them to access your account if you lose your authenticator device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {showBackupCodes && backupCodes.length > 0 && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
                  {backupCodes.map((code, idx) => (
                    <div key={idx} className="text-center py-1 bg-background rounded">
                      {code}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyBackupCodes}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowBackupCodes(false)}>
                    Hide Codes
                  </Button>
                </div>
              </div>
            )}

            {mfaStatus?.mfa_enabled && !showBackupCodes && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  You have {mfaStatus.backup_codes_remaining} backup codes remaining.
                </p>
                <div className="flex gap-2 items-end">
                  <div className="space-y-1">
                    <Label>Enter MFA code to regenerate</Label>
                    <Input
                      type="text"
                      placeholder="000000"
                      value={regenerateCode}
                      onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-32 font-mono"
                      maxLength={6}
                    />
                  </div>
                  <Button variant="outline" onClick={handleRegenerateBackupCodes} disabled={processing || regenerateCode.length !== 6}>
                    {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Generate New Codes
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Email 2FA Settings Component
const EmailMFASettings = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [disableCodeSent, setDisableCodeSent] = useState(false);

  // Fetch email 2FA status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/auth/email-2fa/status`);
        if (response.ok) {
          const data = await response.json();
          setStatus(data);
          setMaskedEmail(data.email || '');
        }
      } catch (error) {
        console.error('Failed to fetch email 2FA status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  // Start setup - sends verification code
  const handleStartSetup = async () => {
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/email-2fa/enable`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setMaskedEmail(data.email);
        setSetupMode(true);
        setCodeSent(true);
        if (data.mocked) {
          toast.info('Email 2FA is in test mode - check server logs for the code');
        } else {
          toast.success('Verification code sent to your email');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start setup');
      }
    } catch (error) {
      toast.error('Failed to send verification code');
    } finally {
      setProcessing(false);
    }
  };

  // Verify setup code
  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/email-2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: verifyCode })
      });
      if (response.ok) {
        toast.success('Email 2FA enabled successfully!');
        setSetupMode(false);
        setVerifyCode('');
        setCodeSent(false);
        // Refresh status
        const statusRes = await apiFetch(`${API_URL}/api/auth/email-2fa/status`);
        if (statusRes.ok) setStatus(await statusRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setProcessing(false);
    }
  };

  // Request disable code
  const handleRequestDisableCode = async () => {
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/email-2fa/send-disable-code`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Verification code sent to your email');
        setDisableMode(true);
        setDisableCodeSent(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Failed to send code');
    } finally {
      setProcessing(false);
    }
  };

  // Disable email 2FA
  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/email-2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: disableCode })
      });
      if (response.ok) {
        toast.success('Email 2FA disabled');
        setDisableMode(false);
        setDisableCode('');
        setDisableCodeSent(false);
        setStatus({ email_2fa_enabled: false });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to disable');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Receive a verification code via email each time you sign in. This is an alternative to app-based 2FA.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <Badge 
            variant={status?.email_2fa_enabled ? "default" : "secondary"}
            className={status?.email_2fa_enabled ? "bg-emerald-500" : ""}
          >
            {status?.email_2fa_enabled ? (
              <>
                <ShieldCheck className="w-3 h-3 mr-1" />
                Enabled
              </>
            ) : (
              <>
                <ShieldOff className="w-3 h-3 mr-1" />
                Disabled
              </>
            )}
          </Badge>
          {status?.email_2fa_enabled && status?.email_2fa_enabled_at && (
            <span className="text-sm text-muted-foreground">
              Enabled on {new Date(status.email_2fa_enabled_at).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Enable Button */}
        {!status?.email_2fa_enabled && !setupMode && (
          <Button onClick={handleStartSetup} disabled={processing} data-testid="enable-email-2fa-btn">
            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Enable Email 2FA
          </Button>
        )}

        {/* Setup Flow */}
        {setupMode && (
          <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
            <h4 className="font-medium">Setup Email Two-Factor Authentication</h4>
            
            <div className="space-y-3">
              {codeSent && (
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">
                    Verification code sent to <strong>{maskedEmail}</strong>
                  </span>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit verification code from your email:
              </p>
              
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-32 font-mono text-center text-lg tracking-widest"
                  maxLength={6}
                  data-testid="email-2fa-verify-code"
                />
                <Button onClick={handleVerifySetup} disabled={processing || verifyCode.length !== 6}>
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verify & Enable
                </Button>
                <Button variant="outline" onClick={() => { setSetupMode(false); setCodeSent(false); setVerifyCode(''); }}>
                  Cancel
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Didn't receive the code? Check your spam folder or{' '}
                <button 
                  className="text-primary hover:underline" 
                  onClick={handleStartSetup}
                  disabled={processing}
                >
                  resend code
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Disable Section */}
        {status?.email_2fa_enabled && !disableMode && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium text-destructive">Disable Email 2FA</h4>
            <p className="text-sm text-muted-foreground">
              To disable email 2FA, we'll send a verification code to your email.
            </p>
            <Button variant="destructive" onClick={handleRequestDisableCode} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Request Disable Code
            </Button>
          </div>
        )}

        {/* Disable Verification */}
        {disableMode && (
          <div className="space-y-4 border rounded-lg p-4 bg-destructive/5">
            <h4 className="font-medium text-destructive">Confirm Disable</h4>
            
            {disableCodeSent && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span className="text-sm">
                  Verification code sent to <strong>{maskedEmail}</strong>
                </span>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code to disable email 2FA:
            </p>
            
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-32 font-mono text-center"
                maxLength={6}
                data-testid="email-2fa-disable-code"
              />
              <Button variant="destructive" onClick={handleDisable} disabled={processing || disableCode.length !== 6}>
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Disable
              </Button>
              <Button variant="outline" onClick={() => { setDisableMode(false); setDisableCodeSent(false); setDisableCode(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Settings() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  // CountBucks platform staff — the Performance + MCP tabs expose platform
  // internals (site-wide API telemetry, server config) and must never render
  // for customer accounts. Backend endpoints are gated the same way.
  const isPlatformOwner = ['elismithcpa@gmail.com', 'elis@countbucks.com', 'eli@countbucks.com', 'eli@bexcpa.com']
    .includes((user?.email || '').toLowerCase());
  // Detect whether to show the full power-user tab set. Hidden by default to
  // keep the workspace settings page clean for end users. Enabled when:
  //   - URL param `?advanced=1`
  //   - localStorage flag `cb_settings_advanced=1` (sticky once enabled)
  //   - workspace_role === 'owner' OR 'admin' (auto-enable for admins so they
  //     can find integrations like Google, Plaid, Receipt-import out of the box)
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('advanced') === '1') {
        localStorage.setItem('cb_settings_advanced', '1');
        return true;
      }
      if (localStorage.getItem('cb_settings_advanced') === '1') return true;
    } catch { /* ignore */ }
    return false;
  });
  // Re-evaluate advanced-visibility whenever the workspace (and therefore role)
  // is known. Owners + admins always get the full tab set.
  useEffect(() => {
    const role = currentWorkspace?.role;
    if (role === 'owner' || role === 'admin') {
      setShowAdvanced(true);
    }
  }, [currentWorkspace?.role]);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'workspace';
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('read_only');
  const [avatarKey, setAvatarKey] = useState(0);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large (max 2MB)'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Only image files allowed'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiFetch(`${API_URL}/api/users/avatar`, { method: 'POST', body: formData });
      if (res.ok) { toast.success('Avatar uploaded!'); setAvatarKey(k => k + 1); }
      else { const d = await res.json(); toast.error(d.detail || 'Upload failed'); }
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  // Invoice Settings State
  const [invoiceTheme, setInvoiceTheme] = useState('blue');
  const [invoiceLogo, setInvoiceLogo] = useState(null);
  const [customColor, setCustomColor] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    website: '',
    tagline: ''
  });
  // Email sender configuration for outbound invoice emails
  const [invoiceEmailFromName, setInvoiceEmailFromName] = useState('');
  const [invoiceEmailReplyTo, setInvoiceEmailReplyTo] = useState('');
  const logoInputRef = useRef(null);

  // Color themes for invoices
  const COLOR_THEMES = [
    { id: 'blue', name: 'Professional Blue', primary: '#2563eb' },
    { id: 'emerald', name: 'Fresh Green', primary: '#059669' },
    { id: 'purple', name: 'Royal Purple', primary: '#7c3aed' },
    { id: 'rose', name: 'Modern Rose', primary: '#e11d48' },
    { id: 'amber', name: 'Warm Amber', primary: '#d97706' },
    { id: 'slate', name: 'Classic Gray', primary: '#475569' },
    { id: 'teal', name: 'Ocean Teal', primary: '#0d9488' },
    { id: 'indigo', name: 'Deep Indigo', primary: '#4f46e5' },
  ];

  // Plaid integration state
  const [plaidStatus, setPlaidStatus] = useState(null);
  const [plaidConfig, setPlaidConfig] = useState({
    client_id: '',
    secret: '',
    env: 'sandbox',
    webhook_url: ''
  });
  const [showSecret, setShowSecret] = useState(false);
  const [savingPlaid, setSavingPlaid] = useState(false);
  const [testingPlaid, setTestingPlaid] = useState(false);
  // Plaid live-diagnostic state — runs a Plaid institutions_get with the
  // active keys and returns the exact error_code/message if something is
  // wrong (INVALID_API_KEYS, INVALID_PRODUCT, ITEM_NOT_SUPPORTED, etc.).
  const [plaidDiag, setPlaidDiag] = useState(null);
  const [runningDiag, setRunningDiag] = useState(false);
  const [clearingPlaid, setClearingPlaid] = useState(false);

  const fetchMembers = async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const response = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members`
      );
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaidStatus = async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await apiFetch(
        `${API_URL}/api/integrations/plaid/status?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPlaidStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch Plaid status:', error);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchPlaidStatus();
  }, [currentWorkspace]);

  // Load invoice settings from backend
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!currentWorkspace) return;
      
      try {
        const response = await apiFetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/invoice-settings`
        );
        
        if (response.ok) {
          const settings = await response.json();
          setInvoiceTheme(settings.theme || 'blue');
          setInvoiceLogo(settings.logo || null);
          setCustomColor(settings.customColor || null);
          setCompanyInfo(settings.companyInfo || { name: currentWorkspace.name || '' });
          setInvoiceEmailFromName(settings.invoice_email_from_name || currentWorkspace.name || '');
          setInvoiceEmailReplyTo(settings.invoice_email_reply_to || '');
        } else {
          // Fallback to localStorage for backwards compatibility
          const savedSettings = localStorage.getItem(`invoice_settings_${currentWorkspace.workspace_id}`);
          if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            setInvoiceTheme(settings.theme || 'blue');
            setInvoiceLogo(settings.logo || null);
            setCustomColor(settings.customColor || null);
            setCompanyInfo(settings.companyInfo || { name: currentWorkspace.name || '' });
          } else {
            setCompanyInfo(prev => ({ ...prev, name: currentWorkspace.name || '' }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch invoice settings:', error);
        // Fallback to localStorage
        const savedSettings = localStorage.getItem(`invoice_settings_${currentWorkspace.workspace_id}`);
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          setInvoiceTheme(settings.theme || 'blue');
          setInvoiceLogo(settings.logo || null);
          setCustomColor(settings.customColor || null);
          setCompanyInfo(settings.companyInfo || { name: currentWorkspace.name || '' });
        }
      }
    };
    
    fetchInvoiceSettings();
  }, [currentWorkspace]);

  // Save invoice settings to backend
  const saveInvoiceSettings = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/invoice-settings`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            theme: invoiceTheme,
            logo: invoiceLogo,
            customColor: customColor,
            companyInfo: companyInfo,
            invoice_email_from_name: invoiceEmailFromName,
            invoice_email_reply_to: invoiceEmailReplyTo,
          })
        }
      );
      
      if (response.ok) {
        toast.success('Invoice settings saved');
        // Also update localStorage as backup
        localStorage.setItem(`invoice_settings_${currentWorkspace.workspace_id}`, JSON.stringify({
          theme: invoiceTheme,
          logo: invoiceLogo,
          customColor: customColor,
          companyInfo: companyInfo
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save invoice settings');
      }
    } catch (error) {
      console.error('Failed to save invoice settings:', error);
      toast.error('Failed to save invoice settings');
    }
  }, [currentWorkspace, invoiceTheme, invoiceLogo, customColor, companyInfo]);

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setInvoiceLogo(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Format phone number as user types (XXX-XXX-XXXX)
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Format with hyphens
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  };

  // Validate phone number (must be 10 digits)
  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Empty is ok (optional field)
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  // Format website URL
  const formatWebsite = (value) => {
    let url = value.trim();
    // Remove any existing protocol for display
    url = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
    return url;
  };

  // Validate website format
  const validateWebsite = (website) => {
    if (!website) return true; // Empty is ok (optional field)
    // Basic website validation - should have at least domain.tld format
    const pattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+([\/\w\.-]*)*\/?$/;
    return pattern.test(website);
  };

  // Handle phone input change
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setCompanyInfo(prev => ({ ...prev, phone: formatted }));
  };

  // Handle website input change
  const handleWebsiteChange = (e) => {
    const formatted = formatWebsite(e.target.value);
    setCompanyInfo(prev => ({ ...prev, website: formatted }));
  };

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});

  // Validate before saving
  const validateAndSaveInvoiceSettings = () => {
    const errors = {};
    
    // Validate phone
    if (companyInfo.phone && !validatePhoneNumber(companyInfo.phone)) {
      errors.phone = 'Phone number must be 10 digits (e.g., 555-123-4567)';
    }
    
    // Validate website
    if (companyInfo.website && !validateWebsite(companyInfo.website)) {
      errors.website = 'Please enter a valid website (e.g., example.com)';
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors before saving');
      return;
    }
    
    saveInvoiceSettings();
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) return;

    try {
      const response = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newMemberEmail.trim(),
            role: newMemberRole,
          }),
        }
      );

      if (response.ok) {
        toast.success('Member added successfully');
        setAddDialogOpen(false);
        setNewMemberEmail('');
        setNewMemberRole('read_only');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add member');
      }
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error('Failed to add member');
    }
  };

  const handleSavePlaidConfig = async () => {
    if (!plaidConfig.client_id || !plaidConfig.secret) {
      toast.error('Client ID and Secret are required');
      return;
    }

    setSavingPlaid(true);
    try {
      const response = await apiFetch(
        `${API_URL}/api/integrations/plaid/configure?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(plaidConfig),
        }
      );

      if (response.ok) {
        toast.success('Plaid configuration saved');
        setPlaidConfig({ ...plaidConfig, client_id: '', secret: '' });
        fetchPlaidStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingPlaid(false);
    }
  };

  const handleClearWorkspacePlaid = async () => {
    const ok = window.confirm(
      "Remove this workspace's Plaid credentials? The workspace will fall back " +
      "to the platform's global Plaid keys. Any already-connected banks stay connected " +
      "— this only clears the API key override."
    );
    if (!ok) return;
    setClearingPlaid(true);
    try {
      const response = await apiFetch(
        `${API_URL}/api/integrations/plaid/configure?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'DELETE' }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Workspace Plaid credentials cleared');
        setPlaidDiag(null);
        setPlaidConfig({ ...plaidConfig, client_id: '', secret: '' });
        fetchPlaidStatus();
      } else {
        toast.error(data.detail || 'Failed to clear credentials');
      }
    } catch (err) {
      toast.error('Failed to clear credentials');
    } finally {
      setClearingPlaid(false);
    }
  };

  const handleTestPlaidConnection = async () => {
    setTestingPlaid(true);
    try {
      const response = await apiFetch(
        `${API_URL}/api/integrations/plaid/test?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Connection successful! Found ${data.accounts_found} accounts.`);
        fetchPlaidStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingPlaid(false);
    }
  };

  // Run live Plaid diagnostic — calls institutions_get against api.plaid.com
  // with the currently-active keys and surfaces the exact Plaid error_code so
  // production-only failures (e.g. "Failed to create link token") can be
  // diagnosed without server-log access.
  const handleRunPlaidDiagnostic = async () => {
    setRunningDiag(true);
    setPlaidDiag(null);
    try {
      const r = await apiFetch(
        `${API_URL}/api/plaid/diagnostic?workspace_id=${currentWorkspace.workspace_id}`,
      );
      const data = await r.json();
      setPlaidDiag(data);
      if (data?.diagnostic === 'ok') {
        toast.success(`Plaid keys are valid for env=${data.env}`);
      } else if (data?.plaid_error_code) {
        toast.error(`Plaid: ${data.plaid_error_code}`);
      } else {
        toast.error(data?.detail || data?.hint || 'Diagnostic failed');
      }
    } catch (e) {
      toast.error('Diagnostic request failed');
    } finally {
      setRunningDiag(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage workspace settings, team members, and integrations
          </p>
        </div>
        <HelpPanel {...settingsHelp} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex-wrap gap-1 p-1">
          <TabsTrigger value="workspace" data-testid="tab-workspace">
            <Building2 className="w-4 h-4 mr-2" />
            Setup
          </TabsTrigger>
          {showAdvanced && isPlatformOwner && (
            <TabsTrigger value="performance" data-testid="tab-performance" className="border border-amber-500/30 data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-amber-500/20">
              <Activity className="w-4 h-4 mr-2 text-amber-500" />
              Performance
            </TabsTrigger>
          )}
          <TabsTrigger value="billing" data-testid="tab-billing" className="border border-violet-500/30 data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-violet-500/20">
            <CreditCard className="w-4 h-4 mr-2 text-violet-500" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="border border-emerald-500/30 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-emerald-500/20">
            <Banknote className="w-4 h-4 mr-2 text-emerald-500" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal" className="border border-blue-500/30 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-blue-500/20">
            <Globe className="w-4 h-4 mr-2 text-blue-500" />
            Client Portal
          </TabsTrigger>
          <TabsTrigger value="site" data-testid="tab-site" className="border border-violet-500/30 data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-violet-500/20">
            <Sparkles className="w-4 h-4 mr-2 text-violet-500" />
            Website
          </TabsTrigger>
          {/* Admin tab — always visible to workspace owners/admins per Feb 30
              2026 user feedback ("admin panel inside of the settings"). The
              standalone /owner-admin route still exists for direct links. */}
          {['owner', 'admin'].includes(currentWorkspace?.role) && (
            <TabsTrigger value="admin" data-testid="tab-admin" className="border border-amber-500/30 data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-amber-500/20">
              <Shield className="w-4 h-4 mr-2 text-amber-500" />
              Admin
            </TabsTrigger>
          )}
          {/* Branding lives at the top level — logo + invoice colors are
              the FIRST thing a new workspace wants to set up. */}
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <FileText className="w-4 h-4 mr-2" />
            Invoices &amp; Branding
          </TabsTrigger>
          {showAdvanced && <>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <Link2 className="w-4 h-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="my-access" data-testid="tab-my-access">
              <Shield className="w-4 h-4 mr-2 text-emerald-500" />
              My Access
            </TabsTrigger>
            {isPlatformOwner && (
            <TabsTrigger value="mcp" data-testid="tab-mcp" className="ml-3 border border-emerald-500/30 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-emerald-500/20">
              <Server className="w-4 h-4 mr-2 text-emerald-500" />
              MCP Server
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-500/20 text-emerald-400 leading-none">NEW</span>
            </TabsTrigger>
            )}
          </>}
        </TabsList>

        {/* Workspace Tab */}
        <TabsContent value="workspace" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle>{currentWorkspace?.name}</CardTitle>
                  <CardDescription>
                    Currency: {currentWorkspace?.currency}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Workspace ID</p>
                  <p className="font-mono text-sm truncate">{currentWorkspace?.workspace_id}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Your Role</p>
                  <Badge className={roleColors[currentWorkspace?.role] || ''}>
                    {currentWorkspace?.role}
                  </Badge>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {currentWorkspace?.created_at 
                      ? new Date(currentWorkspace.created_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dashboard default period — the workspace setting that
              controls which window the Dashboard opens to. `auto` keeps
              the smart-pivot heuristic (recommended). Any explicit
              preset short-circuits the heuristic. */}
          <DashboardDefaultRangeCard
            workspaceId={currentWorkspace?.workspace_id}
            canWrite={['owner', 'admin'].includes(currentWorkspace?.role)}
          />

          {/* Roles Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-muted-foreground" />
                <div>
                  <CardTitle>Roles & Permissions</CardTitle>
                  <CardDescription>
                    Understanding workspace access levels
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { role: 'owner', desc: 'Full access, can manage workspace and members' },
                  { role: 'admin', desc: 'Full access, can manage members' },
                  { role: 'accountant', desc: 'GL, AR, AP access, can view reports' },
                  { role: 'bookkeeper', desc: 'GL, AR, AP access only' },
                  { role: 'read_only', desc: 'Can only view reports' },
                  { role: 'client_portal', desc: 'Limited client access' },
                ].map((item) => (
                  <div key={item.role} className="p-4 rounded-lg border border-border">
                    <Badge className={`${roleColors[item.role]} mb-2`}>
                      {item.role}
                    </Badge>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices & Branding Tab — two-column layout with sticky live preview */}
        <TabsContent value="invoices" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div>
                  <CardTitle>Branding &amp; Invoice Setup</CardTitle>
                  <CardDescription>
                    Your logo, brand color, and company info — these flow through to every invoice,
                    estimate, statement, and client-facing PDF you send.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Logo */}
              <div className="space-y-3">
                <Label className="text-base font-medium flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Company Logo
                </Label>
                <p className="text-sm text-muted-foreground">
                  Appears top-left on every invoice and PDF. PNG or SVG, max 2&nbsp;MB. Transparent
                  background works best.
                </p>
                <div className="flex items-center gap-4">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  {invoiceLogo ? (
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        <img src={invoiceLogo} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                          Change Logo
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setInvoiceLogo(null)}>
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => logoInputRef.current?.click()}>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                  )}
                </div>
              </div>

              {/* Color Theme */}
              <div className="space-y-3 border-t pt-6">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Invoice Color Theme
                </Label>
                <p className="text-sm text-muted-foreground">
                  Choose a color theme for your invoices or set a custom brand color.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {COLOR_THEMES.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => { setInvoiceTheme(theme.id); setCustomColor(null); }}
                      className={`p-3 border rounded-lg text-left transition-all hover:border-primary ${
                        invoiceTheme === theme.id && !customColor ? 'border-primary ring-2 ring-primary/20' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: theme.primary }}
                        />
                        <span className="text-sm font-medium">{theme.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Label>Custom Color:</Label>
                  <Input
                    type="color"
                    value={customColor || '#2563eb'}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={customColor || ''}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#2563eb"
                    className="w-32"
                  />
                  {customColor && (
                    <Button variant="ghost" size="sm" onClick={() => setCustomColor(null)}>
                      Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Company Information */}
              <div className="space-y-4 border-t pt-6">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Information
                </Label>
                <p className="text-sm text-muted-foreground">
                  This information will appear on your invoices.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input 
                      value={companyInfo?.name || ''} 
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline (optional)</Label>
                    <Input 
                      value={companyInfo?.tagline || ''} 
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="Your company tagline"
                    />
                  </div>
                </div>

                {/* Email sender — controls the "From" name + Reply-To on invoice emails */}
                <div className="pt-4 mt-4 border-t" data-testid="invoice-email-sender-section">
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold">Email sender</h4>
                    <p className="text-xs text-muted-foreground">
                      Customers see this name when they receive your invoice email.
                      Replies go to your Reply-To address (not the noreply mailbox).
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>"From" name on invoice emails</Label>
                      <Input
                        value={invoiceEmailFromName}
                        onChange={(e) => setInvoiceEmailFromName(e.target.value)}
                        placeholder={currentWorkspace?.name || 'Your business name'}
                        maxLength={60}
                        data-testid="invoice-email-from-name-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Customers see: <span className="font-mono">{(invoiceEmailFromName || currentWorkspace?.name || 'Your business').replace(/[<>,]/g, '')} &lt;noreply@countbucks.com&gt;</span>
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Reply-To address</Label>
                      <Input
                        type="email"
                        value={invoiceEmailReplyTo}
                        onChange={(e) => setInvoiceEmailReplyTo(e.target.value)}
                        placeholder="billing@yourcompany.com"
                        data-testid="invoice-email-reply-to-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Leave blank to route replies to the workspace owner's email.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address
                  </Label>
                  <Input 
                    value={companyInfo?.address || ''} 
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </Label>
                    <Input 
                      type="email"
                      value={companyInfo?.email || ''} 
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="billing@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone
                    </Label>
                    <Input 
                      value={companyInfo?.phone || ''} 
                      onChange={handlePhoneChange}
                      placeholder="555-123-4567"
                      maxLength={12}
                      className={validationErrors.phone ? 'border-destructive' : ''}
                    />
                    {validationErrors.phone && (
                      <p className="text-xs text-destructive">{validationErrors.phone}</p>
                    )}
                    <p className="text-xs text-muted-foreground">Format: 555-123-4567</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website (optional)
                  </Label>
                  <Input 
                    value={companyInfo?.website || ''} 
                    onChange={handleWebsiteChange}
                    placeholder="example.com"
                    className={validationErrors.website ? 'border-destructive' : ''}
                  />
                  {validationErrors.website && (
                    <p className="text-xs text-destructive">{validationErrors.website}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Format: example.com or www.example.com</p>
                </div>
              </div>

              {/* Save Button */}
              <div className="border-t pt-6">
                <Button onClick={validateAndSaveInvoiceSettings} data-testid="save-invoice-settings">
                  Save Invoice Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>
                See how your invoices will look with the current settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="bg-white rounded-lg border overflow-hidden"
                style={{ maxHeight: '400px', overflow: 'hidden' }}
              >
                {/* Mini Invoice Preview */}
                <div 
                  className="p-6 text-white"
                  style={{ backgroundColor: customColor || COLOR_THEMES.find(t => t.id === invoiceTheme)?.primary || '#2563eb' }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      {invoiceLogo ? (
                        <img src={invoiceLogo} alt="Logo" className="h-10 w-auto rounded bg-white p-1" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-white/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-white/80" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold">{companyInfo.name || 'Your Company'}</h3>
                        {companyInfo.tagline && (
                          <p className="text-white/70 text-xs">{companyInfo.tagline}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <h2 className="text-xl font-bold">INVOICE</h2>
                      <p className="text-white/70 text-sm">#INV-001</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-1">From</p>
                      <p className="font-medium">{companyInfo.name || 'Your Company'}</p>
                      {companyInfo.address && <p className="text-muted-foreground text-xs">{companyInfo.address}</p>}
                      {companyInfo.email && <p className="text-muted-foreground text-xs">{companyInfo.email}</p>}
                      {companyInfo.phone && <p className="text-muted-foreground text-xs">{companyInfo.phone}</p>}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-1">Bill To</p>
                      <p className="font-medium">Sample Customer</p>
                      <p className="text-muted-foreground text-xs">123 Customer St</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
            </div>
            {/* Sticky live invoice preview — updates as the user edits
                logo / color / company info on the left. Hidden on small
                screens (the inline preview inside the Card is enough). */}
            <aside className="hidden lg:block sticky top-20" data-testid="invoice-preview-pane">
              <LiveInvoicePreview
                logo={invoiceLogo}
                customColor={customColor}
                themeId={invoiceTheme}
                themes={COLOR_THEMES}
                company={companyInfo}
              />
            </aside>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground" />
                <div>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    Manage who has access to this workspace
                  </CardDescription>
                </div>
              </div>
              {['owner', 'admin'].includes(currentWorkspace?.role) && (
                <Button onClick={() => setAddDialogOpen(true)} data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.user_id} data-testid={`member-${member.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="relative group">
                            <Avatar className="w-8 h-8" key={avatarKey}>
                              <AvatarImage src={`${API_AVATAR_URL(member.user_id)}?v=${avatarKey}`} alt={member.name} className="object-cover" />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">
                                {member.name?.charAt(0)?.toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            {member.user_id === user?.user_id && (
                              <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer flex items-center justify-center transition-opacity" data-testid="avatar-upload-trigger">
                                <ImageIcon className="w-3.5 h-3.5 text-white" />
                                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                              </label>
                            )}
                          </div>
                          <span className="font-medium">{member.name}</span>
                          {member.user_id === user?.user_id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        <Badge className={roleColors[member.role] || ''}>
                          {member.role}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {['owner', 'admin'].includes(currentWorkspace?.role) && currentWorkspace?.workspace_id && (
            <TemplatePermissionsCard workspaceId={currentWorkspace.workspace_id} />
          )}
        </TabsContent>
        <TabsContent value="integrations" className="space-y-6">
          {/* Receipt auto-import via forwarding address */}
          <ReceiptsForwardingCard workspaceId={currentWorkspace?.workspace_id} />

          {/* Google (Gmail + Calendar) — separate from sign-in flow */}
          <GoogleIntegrationCard />

          {/* Plaid Integration */}
          <Card data-testid="plaid-integration">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle>Plaid</CardTitle>
                    <CardDescription>
                      Connect bank accounts to import transactions
                    </CardDescription>
                  </div>
                </div>
                {plaidStatus?.configured ? (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <XCircle className="w-3 h-3 mr-1" />
                    Not Configured
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {plaidStatus?.configured && (
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Environment</span>
                    <Badge variant="outline">{plaidStatus.env}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Webhook</span>
                    <Badge variant="outline">{plaidStatus.has_webhook ? 'Configured' : 'Not set'}</Badge>
                  </div>
                  {plaidStatus.test_status && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Last Test</span>
                      <Badge variant={plaidStatus.test_status === 'success' ? 'default' : 'destructive'}>
                        {plaidStatus.test_status}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              <div className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="plaid-client-id">Client ID</Label>
                    <Input
                      id="plaid-client-id"
                      type="text"
                      placeholder="Enter Plaid Client ID"
                      value={plaidConfig.client_id}
                      onChange={(e) => setPlaidConfig({ ...plaidConfig, client_id: e.target.value })}
                      data-testid="plaid-client-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="plaid-secret">Secret</Label>
                    <div className="relative">
                      <Input
                        id="plaid-secret"
                        type={showSecret ? 'text' : 'password'}
                        placeholder="Enter Plaid Secret"
                        value={plaidConfig.secret}
                        onChange={(e) => setPlaidConfig({ ...plaidConfig, secret: e.target.value })}
                        data-testid="plaid-secret"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="plaid-env">Environment</Label>
                    <Select 
                      value={plaidConfig.env} 
                      onValueChange={(v) => setPlaidConfig({ ...plaidConfig, env: v })}
                    >
                      <SelectTrigger data-testid="plaid-env">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox</SelectItem>
                        <SelectItem value="development">Development</SelectItem>
                        <SelectItem value="production">Production</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="plaid-webhook">Webhook URL (Optional)</Label>
                    <Input
                      id="plaid-webhook"
                      type="url"
                      placeholder="https://your-domain.com/webhook"
                      value={plaidConfig.webhook_url}
                      onChange={(e) => setPlaidConfig({ ...plaidConfig, webhook_url: e.target.value })}
                      data-testid="plaid-webhook"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleSavePlaidConfig}
                    disabled={savingPlaid || !plaidConfig.client_id || !plaidConfig.secret}
                    data-testid="save-plaid-config"
                  >
                    {savingPlaid ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      'Save Configuration'
                    )}
                  </Button>
                  
                  {plaidStatus?.configured && plaidStatus?.env === 'sandbox' && (
                    <Button 
                      variant="outline"
                      onClick={handleTestPlaidConnection}
                      disabled={testingPlaid}
                      data-testid="test-plaid-connection"
                    >
                      {testingPlaid ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Test Connection</>
                      )}
                    </Button>
                  )}

                  {/* Live diagnostic — works in ALL envs (sandbox/dev/prod) and
                    * surfaces the exact Plaid error_code so failed link-token
                    * creates can be debugged from the browser. */}
                  {plaidStatus?.configured && (
                    <Button
                      variant="outline"
                      onClick={handleRunPlaidDiagnostic}
                      disabled={runningDiag}
                      data-testid="run-plaid-diagnostic"
                    >
                      {runningDiag ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running diagnostic...</>
                      ) : (
                        <><Activity className="w-4 h-4 mr-2" /> Run diagnostic</>
                      )}
                    </Button>
                  )}

                  {/* Recovery escape hatch — clears the workspace-level
                    * override so the workspace falls back to the global
                    * keys. Use when bad credentials were pasted and the
                    * customer doesn't have the originals handy. */}
                  {plaidStatus?.configured && (
                    <Button
                      variant="outline"
                      onClick={handleClearWorkspacePlaid}
                      disabled={clearingPlaid}
                      className="text-rose-600 hover:text-rose-700 border-rose-300"
                      data-testid="clear-workspace-plaid"
                    >
                      {clearingPlaid ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clearing...</>
                      ) : (
                        <>Clear workspace credentials</>
                      )}
                    </Button>
                  )}
                </div>

                {/* Diagnostic result panel */}
                {plaidDiag && (
                  <div
                    className={`p-4 rounded-lg border text-sm space-y-2 ${
                      plaidDiag.diagnostic === 'ok'
                        ? 'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/15 text-emerald-900 dark:text-emerald-200'
                        : 'border-rose-500/30 bg-rose-50 dark:bg-rose-900/15 text-rose-900 dark:text-rose-200'
                    }`}
                    data-testid="plaid-diagnostic-result"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        {plaidDiag.diagnostic === 'ok'
                          ? 'Plaid keys are working ✓'
                          : `Plaid problem: ${plaidDiag.plaid_error_code || plaidDiag.diagnostic}`}
                      </span>
                      <Badge variant="outline" className="text-[10px]">env: {plaidDiag.env}</Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs opacity-90">
                      <span>Source: {plaidDiag.source}</span>
                      <span>Client ID: <code className="font-mono">{plaidDiag.client_id_fingerprint || '<none>'}</code></span>
                      {plaidDiag.institutions_returned !== undefined && (
                        <span>Live institutions returned: {plaidDiag.institutions_returned}</span>
                      )}
                      {plaidDiag.plaid_error_message && (
                        <span className="col-span-full">Plaid message: <em>{plaidDiag.plaid_error_message}</em></span>
                      )}
                    </div>
                    {plaidDiag.hint && (
                      <p className="text-xs leading-relaxed pt-1 border-t border-current/20">
                        {plaidDiag.hint}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Get your Plaid credentials from the{' '}
                  <a 
                    href="https://dashboard.plaid.com/developers/keys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Plaid Dashboard
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <MFASettings user={user} onUpdate={() => window.location.reload()} />
          <EmailMFASettings user={user} />
        </TabsContent>
        {isPlatformOwner && (
        <TabsContent value="mcp" className="space-y-6">
          <MCPSettings />
        </TabsContent>
        )}
        {isPlatformOwner && (
        <TabsContent value="performance" className="space-y-6">
          <PerformanceDashboard />
        </TabsContent>
        )}
        <TabsContent value="billing" className="space-y-6">
          <BillingSettings />
        </TabsContent>
        <TabsContent value="payments" className="space-y-6">
          <StripeConnectCard currentWorkspace={currentWorkspace} />
          <PaymentSettingsCard currentWorkspace={currentWorkspace} />
        </TabsContent>
        <TabsContent value="portal" className="space-y-6">
          <PortalSettingsCard currentWorkspace={currentWorkspace} />
          <RoutingMemoryCard currentWorkspace={currentWorkspace} />
        </TabsContent>
        <TabsContent value="site" className="space-y-6">
          <SiteEditorCard currentWorkspace={currentWorkspace} />
        </TabsContent>
        <TabsContent value="my-access" className="space-y-6">
          <MyAccessPanel />
          {/* iter264 — every member can see their own activity (logins,
              edits, signatures, AI Buck approvals, access requests). Admins
              can view a teammate's via the AdminPanel embed. */}
          <MemberActivityTimeline />
        </TabsContent>

        {/* Admin — owners/admins only. Embeds the standalone AdminPanel so
            users can manage members, access policies, and feature flags
            without leaving Settings. The /owner-admin route still exists
            for direct linking (e.g. from notifications). */}
        <TabsContent value="admin" className="space-y-6" data-testid="tab-content-admin">
          <AdminPanel embedded />
        </TabsContent>
      </Tabs>

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Invite someone to join this workspace. They must have an account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                data-testid="member-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={newMemberRole} onValueChange={setNewMemberRole}>
                <SelectTrigger data-testid="member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="bookkeeper">Bookkeeper</SelectItem>
                  <SelectItem value="read_only">Read Only</SelectItem>
                  <SelectItem value="client_portal">Client Portal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddMember}
              disabled={!newMemberEmail.trim()}
              data-testid="confirm-add-member"
            >
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
